import { captureException } from '@sentry/capacitor';
import { ErrorWithBreadcrumb } from '~/errors';

export interface RequestOptions{
  prefixUrl?: boolean,
  method?: 'get'|'post'|'put'|'delete'|'patch'|string|undefined;
  body?: object;
  isAnonymous?: boolean;
  suppressToasts?: boolean;
}

export function useRequest() {
  const errors = ref<string[]>([]);
  const { t, locale } = useEkI18n();

  async function request<ResponseT>(url: string, options?: RequestOptions) {
    const config = useRuntimeConfig();
    const tokenStore = useTokenStore();
    const { openToast } = useUserMessages();

    errors.value = [];

    if (options === undefined) {
      options = {};
    }

    const {
      method = 'get',
      body = undefined,
      isAnonymous = false,
      prefixUrl = true,
      suppressToasts = false
    } = options;

    const fullUrl = prefixUrl ? config.public.apiBase + url : url;

    const headers = {
      'Content-Type': 'application/json',
      Authorization: '',
      Accept: '*/*',
      localization: locale.value,
      'Request-Source': config.public.requestSource
    };

    try {
      if (!isAnonymous) {
        const accessToken = await tokenStore.getAccessToken();

        if (!accessToken.token) {
          return;
        }

        headers.Authorization = 'Bearer ' + accessToken.token;
      }

      const fetchOptions = {
        method,
        headers,
        body: JSON.stringify(body)
      };

      const response = await fetch(fullUrl, fetchOptions);

      if (response.status !== 200) {
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.Request, message: response.statusText || `Request failed with status ${response.status}` });
      }

      const data = await response.json() as ResponseDTO<ResponseT>;

      validateResponseBody(data);

      if (errors.value.length > 0 && suppressToasts === false) {
        openToast(t('error.generic'), ToastType.ERROR);
      }

      return data;
    } catch (error: unknown) {
      captureException(error);

      if (typeof error === 'string') {
        errors.value.push(error);
      } else if (error instanceof Error) {
        errors.value.push(error.message);
      } else {
        errors.value.push(JSON.stringify(error));
      }

      if (suppressToasts === false) {
        openToast(t('error.generic'), ToastType.ERROR);
      }
    }
  }

  function validateResponseBody<T>(data: ResponseDTO<T>) {
    if (!data.HasErrors) {
      return;
    }

    if (data.ErrorMessage) {
      errors.value.push(data.ErrorMessage);
    }

    if (data.ValidationErrors.length > 0) {
      errors.value.push(...data.ValidationErrors);
    }

    if (Object.keys(data.ModelStateErrors).length > 0) {
      for (const key in data.ModelStateErrors) {
        errors.value.push(key + ': ' + data.ModelStateErrors[key]);
      }
    }
  }

  return {
    errors,
    request
  };
}
